export const DEFAULT_GCC_COUNTRY_CODE = '+971'

/** Local-number hint for phone inputs (country code is selected separately). */
export const PHONE_LOCAL_PLACEHOLDER = '50 123 4567'

export const GCC_COUNTRY_CODES = [
  { code: '+971', label: 'UAE', localLength: 9 },
  { code: '+966', label: 'KSA', localLength: 9 },
  { code: '+973', label: 'Bahrain', localLength: 8 },
  { code: '+974', label: 'Qatar', localLength: 8 },
  { code: '+968', label: 'Oman', localLength: 8 },
  { code: '+965', label: 'Kuwait', localLength: 8 },
]

/**
 * @param {string} value
 */
export function stripToDigits(value) {
  return String(value ?? '').replace(/\D/g, '')
}

/**
 * @param {string} [countryCode]
 */
export function getGccCountryConfig(countryCode = DEFAULT_GCC_COUNTRY_CODE) {
  return GCC_COUNTRY_CODES.find((item) => item.code === countryCode) ?? GCC_COUNTRY_CODES[0]
}

/**
 * @param {string} e164
 */
export function detectCountryFromE164(e164) {
  const digits = stripToDigits(e164)
  const match = GCC_COUNTRY_CODES.find((item) => {
    const codeDigits = item.code.replace(/\D/g, '')
    return digits.startsWith(codeDigits)
  })
  return match?.code ?? DEFAULT_GCC_COUNTRY_CODE
}

/**
 * @param {string} e164
 * @param {string} countryCode
 */
export function e164ToLocalDigits(e164, countryCode) {
  const codeDigits = countryCode.replace(/\D/g, '')
  let digits = stripToDigits(e164)
  if (digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length)
  }
  const { localLength } = getGccCountryConfig(countryCode)
  return digits.slice(0, localLength)
}

/**
 * @param {string} value
 * @param {string} [countryCode]
 */
export function formatLocalPhoneInput(value, countryCode = DEFAULT_GCC_COUNTRY_CODE) {
  const { localLength } = getGccCountryConfig(countryCode)
  const codeDigits = countryCode.replace(/\D/g, '')
  let digits = stripToDigits(value)
  if (digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length)
  }
  digits = digits.replace(/^0+/, '').slice(0, localLength)

  if (localLength === 9) {
    if (digits.length <= 2) return digits
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`
  }

  if (digits.length <= 4) return digits
  return `${digits.slice(0, 4)} ${digits.slice(4)}`
}

/**
 * @param {string} countryCode
 */
function isGccMobileLocal(localDigits, countryCode) {
  const codeDigits = countryCode.replace(/\D/g, '')
  if (codeDigits === '971' || codeDigits === '966') {
    return /^5\d{8}$/.test(localDigits)
  }
  return localDigits.length > 0
}

/**
 * @param {string} localValue
 * @param {string} [countryCode]
 */
export function toE164Phone(localValue, countryCode = DEFAULT_GCC_COUNTRY_CODE) {
  const codeDigits = countryCode.replace(/\D/g, '')
  let digits = stripToDigits(localValue)
  if (digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length)
  }
  digits = digits.replace(/^0+/, '')
  const { localLength } = getGccCountryConfig(countryCode)
  digits = digits.slice(0, localLength)
  if (digits.length !== localLength || !isGccMobileLocal(digits, countryCode)) {
    return ''
  }
  return `+${codeDigits}${digits}`
}

/**
 * @param {string} localValue
 * @param {string} [countryCode]
 */
export function isGccPhoneComplete(localValue, countryCode = DEFAULT_GCC_COUNTRY_CODE) {
  return toE164Phone(localValue, countryCode) !== ''
}

/**
 * @param {string} [countryIso]
 */
export function defaultPhoneCountryForBranch(countryIso) {
  const map = {
    AE: '+971',
    SA: '+966',
    BH: '+973',
    QA: '+974',
    OM: '+968',
    KW: '+965',
  }

  return map[String(countryIso ?? '').toUpperCase()] ?? DEFAULT_GCC_COUNTRY_CODE
}

/**
 * @param {string} e164
 */
export function formatE164Display(e164) {
  if (!e164) return ''
  const countryCode = detectCountryFromE164(e164)
  const local = e164ToLocalDigits(e164, countryCode)
  const formatted = formatLocalPhoneInput(local, countryCode)
  return `${countryCode} ${formatted}`.trim()
}
